// src/pages/About.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tab,
  Tabs,
  Alert,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Code,
  Group,
  Update,
  Security,
  ExpandMore,
  CopyAll,
  Save,
  GitHub,
  Language,
  Email,
  Phone,
  LocationOn,
  Info,
  Warning,
  CheckCircle,
  History,
  Map,
  Description,
  Business,
  Person,
  Download
} from '@mui/icons-material';

import { useI18n } from '../i18n/i18n';
import { useNotifications } from '../context/NotificationContext';
import packageJson from '../../package.json';

// --------- Логіка завантаження даних ------------------------------
async function detectAppVersion() {
  if (packageJson?.version) {
    return packageJson.version;
  }
  try {
    const api = (window.api && window.api.app?.getVersion)
      ? window.api
      : (window.electronAPI && window.electronAPI.app?.getVersion ? window.electronAPI : null);
    if (api?.app?.getVersion) {
      const ver = await api.app.getVersion();
      if (ver) return ver;
    }
  } catch (err) {
    console.warn('Electron API error:', err);
  }
  return '0.0.0';
}

async function loadFullChangelog() {
  try {
    const res = await fetch('/CHANGELOG.md', { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

async function loadRoadmap() {
  try {
    const res = await fetch('/roadmap.md', { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

function getLibrariesInfo() {
  const deps = packageJson.dependencies || {};
  const devDeps = packageJson.devDependencies || {};
  
  const formatDeps = (obj) => 
    Object.entries(obj).map(([name, version]) => ({ 
      name, 
      version: version.replace(/[\^~]/g, '')
    }));

  return {
    deps: formatDeps(deps),
    devDeps: formatDeps(devDeps)
  };
}

// Повний текст ліцензії
const LICENSE_TEXT = {
  es: `ACUERDO DE LICENCIA DE USUARIO FINAL Y AVISO DE PROPIEDAD INTELECTUAL

Licenciante/desarrollador:  
SVH Group UA (Serhii Solodukha), Ucrania.  
Licenciatario/usuario:  
LAVANDERIA INSULAR SL (LAINSA), España.

1. Objeto y titularidad  
SVH Group UA (Serhii Solodukha) conserva todos los derechos de propiedad intelectual sobre el código fuente ("interfaz/UX", backend y arquitectura) y los manuales/guías integrados.  
Toda la información, textos, datos y contenidos introducidos o almacenados en el software son propiedad exclusiva de LAVANDERIA INSULAR SL (LAINSA).

2. Finalidad, gratuidad y futuro comercial  
Este software se entrega gratuitamente, únicamente para fines demostrativos y de evaluación interna, con el objetivo de mostrar cómo la automatización puede ser en el futuro: rápida, sencilla y ecológica.  
LAINSA dispondrá de una licencia gratuita durante este período; cualquier cambio a condiciones comerciales será comunicado por escrito y sólo tendrá efecto futuro con aceptación expresa por ambas partes.

3. Derechos concedidos  
La licencia concedida es no exclusiva, intransferible y sin derecho de sublicenciar. LAINSA puede instalar, ejecutar y utilizar el software en sus operaciones internas.

4. Restricciones  
Se prohíbe:  
- La distribución, venta, sublicencia, cesión a terceros, alquiler;  
- Ingeniería inversa, descompilación, desensamblaje, traducción o creación de obras derivadas;  
- Cualquier uso o difusión externa sin consentimiento escrito del licenciante.  
- Eliminar avisos de derechos de autor o advertencias legales.

5. Manuales y documentación  
Los manuales y guías forman parte del software protegido bajo derechos de autor de SVH Group UA (Serhii Solodukha), y sólo pueden usarse para actividades internas de LAINSA; queda prohibida su publicación o distribución a terceros sin consentimiento.

6. Protección de datos, confidencialidad y DPA  
- Ambas partes se comprometen a mantener la confidencialidad de la información a la que accedan por motivo de esta licencia.  
- El tratamiento de datos personales se realiza conforme al RGPD/LOPDGDD (España/UE) y la Ley de Ucrania, siendo LAVANDERIA INSULAR SL el responsable ("data controller") y SVH Group UA únicamente "data processor" o encargado de tratamiento en actuaciones de soporte.  
- Para cualquier tratamiento de datos personales o soporte, ambas partes firman un Anexo de Encargado de Tratamiento de Datos (DPA) donde se detallan finalidad, duración, categorías de datos, medidas técnicas y transferencia internacional.

7. Actualizaciones, soporte y SLA  
Cualquier soporte, actualizaciones o servicios adicionales serán regulados por un SLA separado acordado por ambas partes. Al instalar actualizaciones, LAINSA acepta posibles nuevas condiciones.

8. Garantía y responsabilidad  
El software se entrega "tal cual" y sin garantía de idoneidad salvo lo estipulado en SLA. SVH Group UA no asume responsabilidad por daños indirectos; la responsabilidad total se limita a lo abonado en los últimos 12 meses (actualmente ningún importe, por su gratuidad), excepto si la ley impide tal limitación.

9. Reclamaciones de terceros  
SVH Group UA garantiza que el software no infringe derechos de terceros. En caso de reclamación, SVH Group UA podrá modificar, sustituir el software o resolver la licencia con devolución proporcional de pagos futuros (si existiesen).

10. Vigencia y terminación  
La licencia es válida hasta revocación, incumplimiento grave, insolvencia, o acuerdo mutuo.  
Al finalizar, LAINSA cesará el uso, eliminará el software, y podrá solicitar exportación de sus datos en formato adecuado.

11. Legislación aplicable y jurisdicción  
La licencia se rige por la legislación de España y la Unión Europea (TRLPI, RGPD, LOPDGDD), y las controversias se resolverán en los juzgados de [Es Castell, España], salvo que expresamente se acuerde otra jurisdicción para centros fuera de la UE (por ejemplo, Ucrania).

12. Acuerdo íntegro  
Este instrumento constituye el acuerdo total en materia de licencia. Toda excepción o modificación deberá constar por escrito y firmada.

La instalación, uso o acceso al software implica aceptación de estas condiciones por el representante autorizado de LAINSA.`,

  uk: `ЛІЦЕНЗІЙНА УГОДА КОРИСТУВАЧА Й ПОВІДОМЛЕННЯ ПРО ІНТЕЛЕКТУАЛЬНУ ВЛАСНІСТЬ

Ліцензіар/розробник:  
SVH Group UA (Сергій Солодуха), Україна  
Ліцензіат/користувач:  
LAVANDERIA INSULAR SL (LAINSA), Іспанія

1. Об'єкт і власність  
SVH Group UA (Сергій Солодуха) зберігає всі права на код ("оболонка/інтерфейс", бекенд, архітектура) та вбудовані посібники/мануали.  
Вся інформація, тексти й дані, внесені або збережені в програмі, є власністю LAVANDERIA INSULAR SL (LAINSA).

2. Призначення, безоплатність і майбутня комерціалізація  
Ця програма надається безкоштовно для демонстраційного та внутрішнього ознайомлення, для показу перспективної автоматизації: швидко, просто, екологічно.  
LAINSA користується безкоштовною ліцензією, і лише письмова угода змінює умови на комерційні.

3. Надані права  
Ліцензія невиключна, невідчужувана, без права субліцензії; LAINSA може встановлювати, запускати і використовувати програму тільки у своїй внутрішній діяльності.

4. Обмеження  
Забороняється:
- поширення, продаж, субліцензія, передача, надання у користування третім;
- реверс-інжиніринг, декомпіляція, розбір, переклад чи створення похідних продуктів (крім випадків, передбачених законом);
- вилучати/змінювати повідомлення про авторське право або юридичні попередження;
- зовнішнє використання або розголошення без письмової згоди Ліцензіара.

5. Посібники і документація  
Вбудовані посібники — власність SVH Group UA (Сергій Солодуха); використовуються лише всередині LAINSA. Забороняється публікація або розповсюдження без письмової згоди.

6. Конфіденційність, персональні дані, DPA  
Сторони зобов'язані дотримуватись конфіденційності отриманих у рамках цієї ліцензії даних.  
Всі персональні дані обробляються згідно GDPR/LOPDGDD (ЄС/Іспанія) та Закону України, причому LAINSA виступає Володільцем (контролером) даних, а SVH Group UA — тільки Обробником у технічних питаннях.  
Додатком укладається DPA із деталями обробки, строками, категоріями даних, заходами безпеки.

7. Оновлення, підтримка, SLA  
Оновлення, підтримка — за окремим SLA між сторонами. Встановлення оновлення може тягнути акцепт нових умов.

8. Гарантії й відповідальність  
ПО надається "як є" без гарантії придатності, якщо не передбачено SLA. Ліцензіар не несе відповідальності за опосередковані збитки; сукупна відповідальність обмежена сплаченими сумами за 12 місяців (на зараз — безкоштовно), окрім випадків, коли закон цього не дозволяє.

9. Претензії третіх осіб  
Ліцензіар гарантує, що програма не порушує IP третіх. У випадку претензій — заміна, модифікація або припинення з пропорційним поверненням (у разі оплати в майбутньому).

10. Строк і припинення  
Ліцензія діє до відкликання, грубого порушення, банкрутства чи письмової згоди. Після завершення LAINSA припиняє користування/видаляє ПЗ, має право отримати свої дані у зручному форматі.

11. Право і юрисдикція  
Угода регулюється законами Іспанії та ЄС (TRLPI, GDPR, LOPDGDD); спори вирішуються у судах [Ес Кастель, Іспанія]. За окремим погодженням для нерезидентів — застосовується право України або арбітраж.

12. Повна угода  
Цей документ — повна угода сторін. Будь-які зміни — лише у письмовій формі.

Використання ПЗ означає прийняття цих умов.`,

  en: `END USER LICENSE AGREEMENT AND IP NOTICE

Licensor/Developer:  
SVH Group UA (Serhii Solodukha), Ukraine  
Licensee/User:  
LAVANDERIA INSULAR SL (LAINSA), Spain

1. Subject and Ownership  
SVH Group UA (Serhii Solodukha) retains all IP rights in the code ("shell/UI", backend, architecture) and built-in manuals/guides.  
All information, texts, data and content entered/stored in the program are the exclusive property of LAVANDERIA INSULAR SL (LAINSA).

2. Purpose, Free Use, Future Commercialization  
This software is provided free of charge for demonstration/internal review only, to showcase the speed, simplicity and eco-friendliness of future automation.  
LAINSA uses the program under a royalty-free license during this period; introduction of commercial terms requires written notice and the consent of both parties.

3. Granted Rights  
The license is non-exclusive, non-transferable and non-sublicensable; LAINSA may install, run and use the software strictly for internal business.

4. Restrictions  
It is prohibited to:
- distribute, sell, sublicense, assign, lease the software to third parties;
- reverse engineer, decompile, disassemble, translate, or create derivative works (except as mandated by law);
- remove/change copyright or legal notices;
- use/disclose externally without written Licensor consent.

5. Manuals and Documentation  
Integrated manuals/guides are owned by SVH Group UA (Serhii Solodukha), permitted for LAINSA's internal use only; external publication/distribution is forbidden without Licensor approval.

6. Confidentiality, Personal Data, DPA  
Both parties must keep confidential any information obtained via this license.  
Any processing of personal data must comply with GDPR/LOPDGDD (EU/Spain) and Ukrainian law, with LAINSA as the data controller and SVH Group UA as processor for support.  
A separate DPA addendum specifies data types, duration, processing purpose and safeguards.

7. Updates, Support, SLA  
All updates/support are governed by a separate SLA. Installing updates may require acceptance of revised terms.

8. Warranties and Liability  
The Software is delivered "as is" with no fitness warranty unless specified in an SLA. Licensor is not liable for indirect damages; total liability is capped at 12 months of paid fees (currently zero, as free), unless law prevents such limitation.

9. Third Party Claims  
Licensor guarantees the Software is non-infringing. If notified otherwise, Licensor may replace, modify or terminate the license with proportional refund (if future premium phase applies).

10. Term and Termination  
This license remains valid until revoked, breached, in insolvency, or by mutual written agreement. LAINSA must cease use and delete the Software upon termination, with right to export its data in a suitable format.

11. Law and Jurisdiction  
This agreement is governed by Spanish and EU law (TRLPI, GDPR, LOPDGDD) and disputes resolved by courts in [Es Castell, Spain], unless parties agree to Ukrainian law/arbitration for non-EU entities.

12. Entire Agreement  
This is the entire license agreement. Any amendments must be in writing.

Installation or use of the Software constitutes acceptance.`,

  ca: `ACORD DE LICÈNCIA D'USUARI FINAL I AVÍS DE PROPIETAT INTEL·LECTUAL

Llicenciant/desenvolupador:  
SVH Group UA (Serhii Solodukha), Ucraïna.  
Llicenciatari/usuari:  
LAVANDERIA INSULAR SL (LAINSA), Espanya.

1. Objecte i titularitat  
SVH Group UA (Serhii Solodukha) conserva tots els drets de propietat intel·lectual sobre el codi font ("interfície/UX", backend i arquitectura) i els manuals/guies integrats.  
Tota la informació, textos, dades i continguts introduïts o emmagatzemats al software són propietat exclusiva de LAVANDERIA INSULAR SL (LAINSA).

2. Finalitat, gratuïtat i futur comercial  
Aquest software es lliura de forma gratuïta, únicament per a fins demostratius i d'avaluació interna, amb l'objectiu de mostrar com l'automatització pot ser en el futur: ràpida, senzilla i ecològica.  
LAINSA disposarà d'una llicència gratuïta durant aquest període; qualsevol canvi a condicions comercials serà comunicat per escrit i només tindrà efecte futur amb acceptació expressa per ambdues parts.

3. Drets concedits  
La llicència concedida és no exclusiva, intransferible i sense dret de sublicenciar. LAINSA pot instal·lar, executar i utilitzar el software en les seves operacions internes.

4. Restriccions  
Es prohibeix:  
- La distribució, venda, sublicència, cessió a tercers, lloguer;  
- Enginyeria inversa, descompilació, desmuntatge, traducció o creació d'obres derivades;  
- Qualsevol ús o difusió externa sense consentiment escrit del llicenciant.  
- Eliminar avisos de drets d'autor o advertències legals.

5. Manuals i documentació  
Els manuals i guies formen part del software protegit sota drets d'autor de SVH Group UA (Serhii Solodukha), i només poden usar-se per a activitats internes de LAINSA; queda prohibida la seva publicació o distribució a tercers sense consentiment.

6. Protecció de dades, confidencialitat i DPA  
- Ambdues parts es comprometen a mantenir la confidencialitat de la informació a la qual accedeixin per motiu d'aquesta llicència.  
- El tractament de dades personals es realitza conforme al RGPD/LOPDGDD (Espanya/UE) i la Llei d'Ucraïna, sent LAVANDERIA INSULAR SL el responsable ("data controller") i SVH Group UA únicament "data processor" o encarregat de tractament en actuacions de suport.  
- Per a qualsevol tractament de dades personals o suport, ambdues parts signen un Annex d'Encarregat de Tractament de Dades (DPA) on es detallen finalitat, durada, categories de dades, mesures tècniques i transferència internacional.

7. Actualitzacions, suport i SLA  
Qualsevol suport, actualitzacions o serveis addicionals seran regulats per un SLA separat acordat per ambdues parts. En instal·lar actualitzacions, LAINSA accepta possibles noves condicions.

8. Garantia i responsabilitat  
El software es lliura "tal qual" i sense garantia d'idoneïtat llevat del estipulat en SLA. SVH Group UA no assumeix responsabilitat per danys indirectes; la responsabilitat total es limita a l'abonat en els últims 12 mesos (actualment cap import, per la seva gratuïtat), excepte si la llei impedeix tal limitació.

9. Reclamacions de tercers  
SVH Group UA garanteix que el software no infringeix drets de tercers. En cas de reclamació, SVH Group UA podrà modificar, substituir el software o resoldre la llicència amb devolució proporcional de pagaments futurs (si existissin).

10. Vigència i terminació  
La llicència és vàlida fins a revocació, incompliment greu, insolvència, o acord mutu.  
En finalitzar, LAINSA cessarà l'ús, eliminarà el software, i podrà sol·licitar exportació de les seves dades en format adequat.

11. Legislació aplicable i jurisdicció  
La llicència es regeix per la legislació d'Espanya i la Unió Europea (TRLPI, RGPD, LOPDGDD), i les controvèrsies es resoldran als jutjats de [Es Castell, Espanya], llevat que expressament s'acordi una altra jurisdicció per a centres fora de la UE (per exemple, Ucraïna).

12. Acord íntegre  
Aquest instrument constitueix l'acord total en matèria de llicència. Qualsevol excepció o modificació haurà de constar per escrit i signada.

La instal·lació, ús o accés al software implica acceptació d'aquestes condicions pel representant autoritzat de LAINSA.`
};

const About = () => {
  const { t } = useI18n();
  const theme = useTheme();
  const { showNotification } = useNotifications();
  
  const [version, setVersion] = useState(packageJson.version || '—');
  const [env, setEnv] = useState('web');
  const [changelog, setChangelog] = useState(null);
  const [fullChangelog, setFullChangelog] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const libs = useMemo(() => getLibrariesInfo(), []);

  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [licenseTab, setLicenseTab] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      const [v, log, fullLog, roadmapData] = await Promise.all([
        detectAppVersion(), 
        loadFullChangelog(),
        loadFullChangelog(),
        loadRoadmap()
      ]);
      
      if (!alive) return;
      
      if (v && v !== '0.0.0') {
        setVersion(v);
        try {
          localStorage.setItem('app_version', v);
        } catch {}
      }
      
      if (log) {
        setChangelog(log.slice(0, 2000));
        setFullChangelog(log);
      }
      
      setRoadmap(roadmapData);
      
      try {
        if (window?.process?.versions?.electron) {
          setEnv('electron');
        } else if (window?.matchMedia('(display-mode: standalone)').matches) {
          setEnv('pwa');
        } else {
          setEnv('web');
        }
      } catch {
        setEnv('web');
      }
      
      setBusy(false);
    })();
    
    return () => { alive = false; };
  }, []);

  const diagnostics = useMemo(() => {
    const lines = [];
    lines.push(`========================================`);
    lines.push(`${t('app_title') || 'LainsaApp'} - ${t('about.diagnostic') || 'Діагностика'}`);
    lines.push(`========================================`);
    lines.push(`${t('about_version')}: ${version}`);
    lines.push(`${t('about_environment')}: ${env}`);
    lines.push(`Час: ${new Date().toLocaleString()}`);
    lines.push(``);
    
    try {
      const v = window.process?.versions || {};
      if (v.electron) {
        lines.push(`Electron: ${v.electron}`);
        lines.push(`Chrome: ${v.chrome || 'n/a'}`);
        lines.push(`Node.js: ${v.node || 'n/a'}`);
        lines.push(``);
      }
    } catch {}
    
    try {
      lines.push(`User Agent: ${navigator.userAgent}`);
      lines.push(`Мова: ${navigator.language}`);
      lines.push(`Онлайн: ${navigator.onLine ? 'Так' : 'Ні'}`);
      lines.push(``);
    } catch {}
    
    try {
      lines.push(`Мова інтерфейсу: ${localStorage.getItem('lang') || 'es'}`);
    } catch {}
    
    lines.push(`========================================`);
    lines.push(`Package Info:`);
    lines.push(`Назва: ${packageJson.name}`);
    lines.push(`Автор: ${packageJson.author}`);
    lines.push(`Опис: ${packageJson.description}`);
    
    return lines.join('\n');
  }, [version, env, t]);

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnostics);
      showNotification(t('about_copied'), 'success');
    } catch {
      showNotification(t('about_copy_fail'), 'error');
    }
  };

  const saveDiagnosticsFile = () => {
    try {
      const blob = new Blob([diagnostics], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `lainsa_diagnostics_${timestamp}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification(t('about_save'), 'success');
    } catch (e) {
      showNotification(e.message, 'error');
    }
  };

  const openDialog = (title, content) => {
    setDialogTitle(title);
    setDialogContent(content);
    setDialogOpen(true);
  };

  const features = [
    {
      icon: <Code />,
      title: t('about.features.modernTech.title'),
      description: t('about.features.modernTech.description'),
      color: 'primary'
    },
    {
      icon: <Group />,
      title: t('about.features.team.title'),
      description: t('about.features.team.description'),
      color: 'secondary'
    },
    {
      icon: <Update />,
      title: t('about.features.updates.title'),
      description: t('about.features.updates.description'),
      color: 'success'
    },
    {
      icon: <Security />,
      title: t('about.features.security.title'),
      description: t('about.features.security.description'),
      color: 'warning'
    }
  ];

  const systemStatus = [
    { component: t('about.system.frontend'), status: 'operational', icon: <CheckCircle color="success" /> },
    { component: t('about.system.api'), status: 'operational', icon: <CheckCircle color="success" /> },
    { component: t('about.system.database'), status: 'degraded', icon: <Warning color="warning" /> },
    { component: t('about.system.ml'), status: 'operational', icon: <CheckCircle color="success" /> },
    { component: t('about.system.notifications'), status: 'operational', icon: <Info color="success" /> },
    { component: t('about.system.ai'), status: 'maintenance', icon: <Info color="info" /> },
    { component: t('about.system.incidents'), status: 'maintenance', icon: <Info color="info" /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" gutterBottom sx={{ 
          fontWeight: 'bold',
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
        }}>
          {t('about_title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          {t('app_subtitle')}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Chip 
            icon={<Info />}
            label={`v${version}`}
            color="primary" 
            variant="filled"
          />
          <Chip 
            icon={<Update />}
            label={`${t('about_environment')}: ${env}`}
            color="secondary" 
            variant="outlined"
          />
          <Chip 
            icon={<Business />}
            label={t('about_company_type')} 
            color="info" 
            variant="outlined"
          />
        </Stack>
      </Box>

      {busy && <LinearProgress sx={{ mb: 4 }} />}

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Overview Card */}
          <Card sx={{ mb: 4, background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Description color="primary" sx={{ mr: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                  {t('about.overview.title')}
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                {t('app_subtitle')} - {t('about_company_type')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {packageJson.description || t('about.overview.description')}
              </Typography>
            </CardContent>
          </Card>

          {/* Features Grid */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[4],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box sx={{ color: `${feature.color}.main`, mb: 1, fontSize: '2.5rem' }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Changelog Section */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <History color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    {t('about_changelog')}
                  </Typography>
                </Box>
                {fullChangelog && (
                  <Button
                    startIcon={<Download />}
                    onClick={() => openDialog(t('about_changelog'), fullChangelog)}
                    variant="outlined"
                    size="small"
                  >
                    {t('about.view_full')}
                  </Button>
                )}
              </Box>
              
              {changelog ? (
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '0.875rem',
                    bgcolor: 'grey.50',
                    color: 'grey.900',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  {changelog}
                  {changelog.length >= 2000 && (
                    <Box sx={{ mt: 1, fontStyle: 'italic', color: 'grey.600' }}>
                      ... {t('common.more')}
                    </Box>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t('common.loading')} {t('about_changelog')}...
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Roadmap Section */}
          {roadmap && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Map color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                      {t('about.roadmap.title')}
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<Download />}
                    onClick={() => openDialog(t('about.roadmap.title'), roadmap)}
                    variant="outlined"
                    size="small"
                  >
                    {t('about.view_full')}
                  </Button>
                </Box>
                
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '0.875rem',
                    bgcolor: 'primary.50',
                    color: 'primary.900',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.200',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  {roadmap.slice(0, 1500)}
                  {roadmap.length >= 1500 && (
                    <Box sx={{ mt: 1, fontStyle: 'italic', color: 'primary.600' }}>
                      ... {t('common.more')}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* System Status */}
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Update color="primary" />
                {t('about.system_status.title')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('about.system.component')}</TableCell>
                      <TableCell align="center">{t('common.status')}</TableCell>
                      <TableCell align="right">{t('common.icon')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {systemStatus.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {row.component}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={t(`about.system.status.${row.status}`)} 
                            color={
                              row.status === 'operational' ? 'success' :
                              row.status === 'degraded' ? 'warning' :
                              row.status === 'maintenance' ? 'info' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row.icon}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Contact & Company Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  {t('about_company')}
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                {t('about_company_name')}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                <Person color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  {t('about_author')}
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                {packageJson.author || 'Serhii Solodukha'}
              </Typography>

              <Stack spacing={1} sx={{ mt: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<Email />}
                  href="mailto:svhgroupua@gmail.com"
                  fullWidth
                >
                  {t('about_contact')}
                </Button>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<Language />}
                  onClick={() => window.open('https://ann', '_blank')}
                  fullWidth
                >
                  {t('about_website')}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Diagnostic Tools */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('about.diagnostic')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('about_hint')}
              </Typography>
              
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyAll />}
                  onClick={copyDiagnostics}
                  fullWidth
                >
                  {t('about_copy')}
                </Button>
                
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Save />}
                  onClick={saveDiagnosticsFile}
                  fullWidth
                >
                  {t('about_save')}
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Description />}
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() => setLicenseDialogOpen(true)}
                >
                  {t('about_program_license')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dependencies Accordion */}
      <Accordion sx={{ mt: 4 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            {t('about_licenses_title')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('about_licenses_desc')}
          </Typography>
          
          {libs.deps.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('about_dep_runtime')} ({libs.deps.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('about_dep_name')}</TableCell>
                      <TableCell align="right">{t('about_dep_ver')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {libs.deps.map((dep, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {dep.name}
                        </TableCell>
                        <TableCell align="right">{dep.version}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {libs.devDeps.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('about_dep_dev')} ({libs.devDeps.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('about_dep_name')}</TableCell>
                      <TableCell align="right">{t('about_dep_ver')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {libs.devDeps.map((dep, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {dep.name}
                        </TableCell>
                        <TableCell align="right">{dep.version}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Full Content Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              m: 0,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            {dialogContent}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* License Dialog */}
      <Dialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Tabs
            value={licenseTab}
            onChange={(_, newValue) => setLicenseTab(newValue)}
            variant="fullWidth"
            textColor="inherit"
            indicatorColor="secondary"
          >
            <Tab label="Español" />
            <Tab label="Українська" />
            <Tab label="English" />
            <Tab label="Català" />
          </Tabs>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              minHeight: '60vh',
              maxHeight: '75vh',
              overflow: 'auto',
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                m: 0,
              }}
            >
              {LICENSE_TEXT[licenseTab === 0 ? 'es' : licenseTab === 1 ? 'uk' : licenseTab === 2 ? 'en' : 'ca']}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLicenseDialogOpen(false)} color="primary">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default About;